import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BASE_URL } from "../../utils/baseURL";
import { setAlert } from "./alert.slice";
import axiosInstance from "../../utils/axiosInstance";
import Cookies from 'js-cookie';

const initialState = {
    subcategories: [],
    success: false,
    message: '',
    loading: false,
    error: null,
    deleteStatus: false
};

const handleErrors = (error, dispatch, rejectWithValue) => {
    const errorMessage = error.response?.data?.message || 'An error occurred';
    dispatch(setAlert({ text: errorMessage, color: 'error' }));
    return rejectWithValue(error.response?.data || { message: errorMessage });
};

export const getSubcategories = createAsyncThunk(
    'subcategories/get',
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(BASE_URL + '/allSubCategory', {
                headers: {
                    Authorization: `Bearer ${Cookies.get("accessToken")}`
                }
            });
            // console.log(response.data);
            return { subCategories: response.data.subCategories, deleteStatus: response.data.deleteStatus };
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const getSubcategoriesByCategory = createAsyncThunk(
    'getSubcategoriesByCategory/get',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(BASE_URL + '/getSubCategory/' + id, {
                headers: {
                    Authorization: `Bearer ${Cookies.get("accessToken")}`
                }
            });
            return response.data.subCategories;
        } catch (error) {
            return handleErrors(dispatch);
        }
    }
);

export const addSubcategories = createAsyncThunk(
    'subcategories/add',
    async (data, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(BASE_URL + '/addSubCategory', data, {
                headers: {
                    Authorization: `Bearer ${Cookies.get("accessToken")}`
                }
            });
            dispatch(setAlert({ text: response.data.message, color: 'success' }));
            dispatch(getSubcategories());
            return response.data.subCategories;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const deleteSubcategories = createAsyncThunk(
    'subcategories/delete',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(`${BASE_URL}/deleteSubCategory/${id}`, {
                headers: {
                    Authorization: `Bearer ${Cookies.get("accessToken")}`
                }
            });
            dispatch(setAlert({ text: response.data.message, color: 'success' }));
            return id;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const editSubcategories = createAsyncThunk(
    'subcategories/edit',
    async (data, { dispatch, rejectWithValue }) => {
        try {
            const { _id, ...rest } = data;
            const response = await axiosInstance.put(`${BASE_URL}/updateSubCategory/${_id}`, rest, {
                headers: {
                    Authorization: `Bearer ${Cookies.get("accessToken")}`
                }
            });
            dispatch(setAlert({ text: response.data.message, color: 'success' }));
            dispatch(getSubcategories());
            return response.data.subCategories;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

const subcategoriesSlice = createSlice({
    name: 'subcategories',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getSubcategories.pending, (state) => {
                state.loading = true;
                state.message = 'Fetching subcategories...';
            })
            .addCase(getSubcategories.fulfilled, (state, action) => {
                // console.log(action.payload);
                state.loading = false;
                state.success = true;
                state.message = action.payload?.message || 'Subcategories fetched successfully';
                state.subcategories = action.payload.subCategories;
                state.deleteStatus = action.payload.deleteStatus;
            })
            .addCase(getSubcategories.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to fetch subcategories';
            })
            .addCase(getSubcategoriesByCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = action.payload?.message || 'Subcategories fetched successfully';
                state.subcategories = action.payload;
            })
            .addCase(getSubcategoriesByCategory.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to fetch subcategories';
            })
            .addCase(addSubcategories.fulfilled, (state, action) => {
                state.subcategories = state.subcategories.concat(action.payload);
                state.message = 'Subcategory added successfully';
            })
            .addCase(addSubcategories.rejected, (state, action) => {
                state.message = action.payload?.message || 'Failed to add subcategory';
            })
            .addCase(deleteSubcategories.fulfilled, (state, action) => {
                state.subcategories = state.subcategories.filter((v) => v._id !== action.payload);
                state.message = 'Subcategory deleted successfully';
            })
            .addCase(deleteSubcategories.rejected, (state, action) => {
                state.message = action.payload?.message || 'Failed to delete subcategory';
            })
            .addCase(editSubcategories.fulfilled, (state, action) => {
                state.subcategories = state.subcategories.map((v) =>
                    v._id === action.payload._id ? action.payload : v
                );
                state.message = 'Subcategory updated successfully';
            })
            .addCase(editSubcategories.rejected, (state, action) => {
                state.message = action.payload?.message || 'Failed to update subcategory';
            });
    }
});

export default subcategoriesSlice.reducer;
